import React, { useState, useMemo, useRef } from 'react';
import {
  Table,
  Tag,
  Button,
  Spin,
  Collapse,
  Input,
  InputNumber,
  Select,
  Space,
  DatePicker,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { DeletedMaturityScheduleResponse } from '../../model/DeletedMaturitySchedule.model';
import {
  formatCurrency,
  getStatusColor,
  getStatusText,
} from './MaturityTableUtils';
import dayjs from 'dayjs';
import { EyeOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import DeletedTransactionSubTable from './DeletedTransactionSubTable';

const { Panel } = Collapse;

interface DeletedMaturityTableProps {
  deletedSchedules: DeletedMaturityScheduleResponse[];
  loading: boolean;
}

// Reusable number filter component for specific amounts
const NumberFilter: React.FC<{
  setSelectedKeys: (keys: React.Key[]) => void;
  selectedKeys: React.Key[];
  confirm: () => void;
  clearFilters?: () => void;
  placeholder?: string;
}> = ({
  setSelectedKeys,
  selectedKeys,
  confirm,
  clearFilters,
  placeholder = 'Tìm theo số tiền',
}) => {
    const formatNumber = (value: string | number | undefined) => {
      if (!value) return '';
      return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const parseNumber = (value: string | undefined) => {
      if (!value || value.trim() === '') return 0;
      const cleaned = value.replace(/\$\s?|(,*)/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    };

    return (
      <div style={{ padding: 8 }}>
        <InputNumber
          placeholder={placeholder}
          value={
            selectedKeys[0] ? parseFloat(selectedKeys[0] as string) : undefined
          }
          onChange={(value) => setSelectedKeys(value ? [value.toString()] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block', width: '100%' }}
          formatter={formatNumber}
          parser={parseNumber}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
        </Space>
      </div>
    );
  };

// Reusable number filter logic for exact match
const numberFilter = (value: any, amount: number) => {
  if (!value || value === '') return true;
  const searchValue = parseFloat(value.toString());
  if (isNaN(searchValue)) return true;
  return amount === searchValue;
};

const DeletedMaturityTable: React.FC<DeletedMaturityTableProps> = ({
  deletedSchedules,
  loading,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [tableKey, setTableKey] = useState<number>(0);
  const tableRef = useRef<any>(null);

  // Handle expand/collapse
  const handleExpand = (
    expanded: boolean,
    record: DeletedMaturityScheduleResponse
  ) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.id));
    }
  };

  // Check if row is expanded
  const isRowExpanded = (recordId: number) => {
    return expandedRowKeys.includes(recordId);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setTableKey((prev) => prev + 1);
  };

  // Create filter options from data
  const filterOptions = useMemo(() => {
    const employees = Array.from(
      new Set(
        deletedSchedules
          .flatMap((schedule) => schedule.employees || [])
          .map((emp) => emp.name)
      )
    ).map((name) => ({ text: name, value: name }));

    const creators = Array.from(
      new Set(
        deletedSchedules
          .map((schedule) => schedule.createdByName)
          .filter(Boolean)
      )
    ).map((name) => ({ text: name, value: name }));

    const deleters = Array.from(
      new Set(
        deletedSchedules
          .map((schedule) => schedule.deletedByName)
          .filter(Boolean)
      )
    ).map((name) => ({ text: name, value: name }));

    const banks = Array.from(
      new Set(
        deletedSchedules
          .map((schedule) => schedule.creditCard?.bank?.name)
          .filter(Boolean)
      )
    ).map((name) => ({ text: name, value: name }));

    return { employees, creators, deleters, banks };
  }, [deletedSchedules]);

  const columns: ColumnsType<DeletedMaturityScheduleResponse> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => (
        <span className="font-mono text-sm text-gray-600">{id}</span>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <InputNumber
            placeholder="Tìm theo ID"
            value={selectedKeys[0] as number}
            onChange={(value) =>
              setSelectedKeys(value ? [value.toString()] : [])
            }
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block', width: '100%' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters && clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (!value || value === '') return true;
        const searchValue = parseInt(value.toString());
        if (isNaN(searchValue)) return true;
        return record.id === searchValue;
      },
    },
    {
      title: 'Mở rộng',
      key: 'expand',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          type="text"
          onClick={() => handleExpand(!isRowExpanded(record.id), record)}
          className={`${isRowExpanded(record.id)
            ? 'text-blue-600 bg-blue-50'
            : 'text-gray-500 hover:text-blue-600'
            }`}
        />
      ),
    },

    {
      title: 'Ngày đáo hạn',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            placeholder="Chọn ngày đáo hạn"
            value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
            onChange={(date: dayjs.Dayjs | null) => {
              if (date) {
                setSelectedKeys([date.format('YYYY-MM-DD')]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ width: 200, marginBottom: 8, display: 'block' }}
            disabledDate={(current: dayjs.Dayjs) => {
              const currentMonth = dayjs();
              return !current.isSame(currentMonth, 'month');
            }}
            allowClear
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters && clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (!value || value === '') return true;
        try {
          const filterDate = dayjs(value as string);
          const recordDate = dayjs(record.dueDate);
          return recordDate.isSame(filterDate, 'day');
        } catch (error) {
          console.error('Date filter error:', error);
          return true;
        }
      },
    },
    {
      title: 'Số tiền đáo hạn',
      dataIndex: 'repaymentAmount',
      key: 'repaymentAmount',
      width: 140,
      render: (amount: number) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(amount)}
        </span>
      ),
      filterDropdown: (props: FilterDropdownProps) => (
        <NumberFilter {...props} placeholder="Số tiền đáo hạn" />
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) => numberFilter(value, record.repaymentAmount),
    },
    {
      title: 'Thông tin thẻ',
      key: 'creditCard',
      width: 200,
      render: (_, record) => {
        const card = record.creditCard;
        if (!card) return <span className="text-gray-400">—</span>;

        const formatCardNumber = (cardNumber: string) => {
          if (!cardNumber || cardNumber.length < 4) return 'N/A';
          return `***${cardNumber.slice(-4)}`;
        };

        return (
          <div className="text-sm">
            <div className="font-medium">
              {formatCardNumber(card.cardNumber)}
            </div>
            <div className="text-gray-600">{card.cardHolderName || 'N/A'}</div>
            <div className="text-gray-500">{card.bank?.name || 'N/A'}</div>
          </div>
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm theo 4 số cuối hoặc tên chủ thẻ"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Select
            placeholder="Chọn ngân hàng"
            value={selectedKeys[1] || undefined}
            onChange={(value) =>
              setSelectedKeys([selectedKeys[0] || '', value || ''])
            }
            style={{ marginBottom: 8, display: 'block', width: '100%' }}
            options={[
              { value: '', label: 'Tất cả ngân hàng' },
              ...filterOptions.banks,
            ]}
            allowClear
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters && clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (!value || !Array.isArray(value)) return true;

        const [searchText, bankFilter] = value;
        const card = record.creditCard;

        // If no filters are set, show all
        if (!searchText && !bankFilter) return true;

        let matches = true;

        if (searchText && searchText.trim() !== '') {
          const search = searchText.toLowerCase().trim();
          const cardNumber = card?.cardNumber || '';
          const cardHolderName = card?.cardHolderName || '';
          const last4Digits = cardNumber.slice(-4);

          matches =
            last4Digits.includes(search) ||
            cardHolderName.toLowerCase().includes(search);
        }

        if (bankFilter && bankFilter !== '' && bankFilter !== 'all') {
          matches = matches && card?.bank?.name === bankFilter;
        }

        return matches;
      },
    },
    {
      title: 'Nhân viên',
      key: 'employees',
      width: 150,
      render: (_, record) => (
        <div className="text-sm">
          {record.employees?.map((emp) => (
            <Tag key={emp.id} className="mb-1">
              {emp.name}
            </Tag>
          ))}
        </div>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Select
            mode="multiple"
            placeholder="Chọn nhân viên"
            value={selectedKeys}
            onChange={(value) => setSelectedKeys(value)}
            style={{ width: 200, marginBottom: 8 }}
            options={filterOptions.employees}
          />
          <div>
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Lọc
              </Button>
              <Button
                onClick={() => clearFilters && clearFilters()}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </Space>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (!value || !Array.isArray(value) || value.length === 0) return true;

        const employeeNames = record.employees?.map((emp) => emp.name) || [];
        return value.some((selectedName) =>
          employeeNames.includes(selectedName as string)
        );
      },
    },
    {
      title: 'Người tạo',
      dataIndex: 'createdByName',
      key: 'createdByName',
      width: 120,
      filters: filterOptions.creators,
      onFilter: (value, record) => {
        if (!value || value === '') return true;
        return record.createdByName === value;
      },
    },
    {
      title: 'Người xóa',
      dataIndex: 'deletedByName',
      key: 'deletedByName',
      width: 120,
      render: (name: string) =>
        name ? (
          <span>{name}</span>
        ) : (
          <div className="text-center w-full">
            <span className="text-gray-400 text-xs">—</span>
          </div>
        ),
      filters: filterOptions.deleters,
      onFilter: (value, record) => {
        if (!value || value === '') return true;
        return record.deletedByName === value;
      },
    },
    {
      title: 'Tổng nạp',
      dataIndex: 'totalDepositAmount',
      key: 'totalDepositAmount',
      width: 120,
      render: (amount: number) => (
        <span className="text-green-600 font-medium">
          {formatCurrency(amount || 0)}
        </span>
      ),
      filterDropdown: (props: FilterDropdownProps) => (
        <NumberFilter {...props} placeholder="Tổng nạp" />
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        numberFilter(value, record.totalDepositAmount || 0),
    },
    {
      title: 'Tổng rút',
      dataIndex: 'totalWithdrawalAmount',
      key: 'totalWithdrawalAmount',
      width: 120,
      render: (amount: number) => (
        <span className="text-red-600 font-medium">
          {formatCurrency(amount || 0)}
        </span>
      ),
      filterDropdown: (props: FilterDropdownProps) => (
        <NumberFilter {...props} placeholder="Tổng rút" />
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        numberFilter(value, record.totalWithdrawalAmount || 0),
    },
    {
      title: 'Tổng thanh toán',
      dataIndex: 'totalPaymentAmount',
      key: 'totalPaymentAmount',
      width: 140,
      render: (amount: number) => (
        <span className="text-purple-600 font-medium">
          {formatCurrency(amount || 0)}
        </span>
      ),
      filterDropdown: (props: FilterDropdownProps) => (
        <NumberFilter {...props} placeholder="Tổng thanh toán" />
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        numberFilter(value, record.totalPaymentAmount || 0),
    },
    {
      title: 'Giao dịch liên quan',
      key: 'relatedData',
      width: 140,
      render: (_, record) => (
        <div className="text-sm">
          <div>GD: {record.deletedTransactionsCount || 0}</div>
          <div>TT: {record.deletedPaymentsCount || 0}</div>
        </div>
      ),
    },
    {
      title: 'Thời gian xóa',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 140,
      render: (deletedAt: string) => (
        <div className="text-sm text-gray-600">
          {dayjs(deletedAt).format('DD-MM-YYYY HH:mm')}
        </div>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            showTime
            value={
              selectedKeys[0] && selectedKeys[1]
                ? [
                  dayjs(selectedKeys[0] as string),
                  dayjs(selectedKeys[1] as string),
                ]
                : undefined
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setSelectedKeys([
                  dates[0].format('YYYY-MM-DD HH:mm:ss'),
                  dates[1].format('YYYY-MM-DD HH:mm:ss'),
                ]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters && clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (
          !value ||
          !Array.isArray(value) ||
          value.length !== 2 ||
          !value[0] ||
          !value[1]
        ) {
          return true;
        }

        try {
          const recordTime = dayjs(record.deletedAt);
          const startTime = dayjs(value[0]);
          const endTime = dayjs(value[1]);

          if (
            !recordTime.isValid() ||
            !startTime.isValid() ||
            !endTime.isValid()
          ) {
            return true;
          }

          return (
            (recordTime.isAfter(startTime) || recordTime.isSame(startTime)) &&
            (recordTime.isBefore(endTime) || recordTime.isSame(endTime))
          );
        } catch (error) {
          console.error('Date range filter error:', error);
          return true;
        }
      },
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      render: (notes: string) => (
        <div
          className="text-sm text-gray-600 max-w-[180px] truncate"
          title={notes}
        >
          {notes || '—'}
        </div>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm trong ghi chú"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters && clearFilters()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (!value || value === '') return true;
        const notes = record.notes || '';
        const searchText = (value as string).toLowerCase().trim();
        return notes.toLowerCase().includes(searchText);
      },
    },
  ];

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Spin spinning={loading}>
      <div className="mb-3 flex justify-end">
        <Button
          icon={<ReloadOutlined />}
          onClick={handleClearAllFilters}
          type="text"
          size="small"
          className="text-gray-500 hover:text-blue-600 hover:bg-blue-50 border-0 shadow-none transition-all duration-200 flex items-center gap-1"
          title="Xóa tất cả bộ lọc"
        >
          <span className="text-xs">Xoá bộ lọc</span>
        </Button>
      </div>
      <div
        ref={containerRef}
        style={{ maxHeight: '70vh', overflowY: 'auto', position: 'relative' }}
      >
        <Table
          key={tableKey}
          ref={tableRef}
          columns={columns}
          dataSource={deletedSchedules.sort((a, b) => b.id - a.id)}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} kỳ đáo hạn đã xóa`,
          }}
          scroll={{ x: 1600 }}
          sticky={{ offsetHeader: 0, getContainer: () => containerRef.current! }}
          className="bg-white rounded-lg"
          size="middle"
          rowClassName="group hover:bg-gray-50"
          expandable={{
            expandedRowRender: (record) => (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-4 text-gray-700">
                  Chi tiết giao dịch và thanh toán đã bị xóa
                </h4>
                <DeletedTransactionSubTable maturityScheduleId={record.id} />
              </div>
            ),
            rowExpandable: () => true,
            expandRowByClick: false,
            expandedRowKeys: expandedRowKeys,
            onExpand: handleExpand,
            showExpandColumn: false,
          }}
        />
      </div>
    </Spin>
  );
};

export default DeletedMaturityTable;
